import { useState } from "react";
import { useSections, useBeats, useAnnotationsByLayer, useCreateAnnotation, useUpdateAnnotation } from "../../../lib/presentationQueries";
import { useAutosaveEntity } from "../../../hooks/useAutosaveEntity";
import AutosaveField from "./AutosaveField";
import AutosaveStatus from "./AutosaveStatus";

const LAYERS = [
  { key: "voice", label: "Voice" },
  { key: "body_posture", label: "Body & Posture" },
  { key: "gestures", label: "Gestures" },
  { key: "gaze", label: "Gaze" },
  { key: "expression", label: "Expression" },
  { key: "stage_and_space", label: "Stage & Space" },
  { key: "personal_cues", label: "Personal Cues" },
];

function BeatAnnotationRow({ workspaceId, presId, beat, layerKey, existing }) {
  const createAnnotation = useCreateAnnotation(workspaceId, presId, beat.beat_id, layerKey);
  const updateAnnotation = useUpdateAnnotation(workspaceId, presId);
  const auto = useAutosaveEntity({
    initialValues: { note_text: existing?.note_text || "" },
    revision: existing?.revision || 1,
    onSave: ({ note_text, expected_revision, idempotency_key }) =>
      existing
        ? updateAnnotation.mutateAsync({ danId: existing.dan_id, note_text, expected_revision, idempotency_key })
        : createAnnotation.mutateAsync({ layer_key: layerKey, note_text }),
  });
  return (
    <div className="border border-ink/10 bg-white/40 rounded-lg p-3 mb-2" data-testid={`delivery-annotation-${beat.beat_id}`}>
      <p className="text-sm text-ink-soft mb-2 truncate">{beat.title || beat.body_text || "(untitled beat)"}</p>
      <AutosaveField value={auto.values.note_text} onChange={(v) => auto.setField("note_text", v)} placeholder="Delivery note…" testId={`delivery-annotation-${beat.beat_id}-note`} />
      <AutosaveStatus status={auto.status} onRetry={auto.retry} testId={`delivery-annotation-${beat.beat_id}-autosave-status`} />
    </div>
  );
}

function SectionBeats({ workspaceId, presId, sectionId, sectionTitle, layerKey, annotationsByBeat }) {
  const { data } = useBeats(workspaceId, presId, sectionId);
  const beats = data?.items || [];
  if (beats.length === 0) return null;
  return (
    <div className="mb-5" data-testid={`delivery-section-${sectionId}`}>
      <p className="text-xs uppercase tracking-wide text-ink-soft mb-2">{sectionTitle}</p>
      {beats.map((beat) => (
        <BeatAnnotationRow key={beat.beat_id} workspaceId={workspaceId} presId={presId} beat={beat} layerKey={layerKey} existing={annotationsByBeat[beat.beat_id]} />
      ))}
    </div>
  );
}

export default function DeliveryLayersArea({ workspaceId, presId, activeStructureVersionId }) {
  const [layerKey, setLayerKey] = useState("voice");
  const { data: sectionsData } = useSections(workspaceId, presId, activeStructureVersionId);
  const sections = sectionsData?.items || [];
  const { data: annotationsData } = useAnnotationsByLayer(workspaceId, presId, layerKey);
  const annotationsByBeat = Object.fromEntries((annotationsData?.items || []).map((a) => [a.beat_id, a]));

  return (
    <div data-testid="presentation-delivery-layers-area">
      <h2 className="font-serif text-xl text-ink mb-4">Delivery Layers</h2>
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="delivery-layer-picker">
        {LAYERS.map((l) => (
          <button
            key={l.key} onClick={() => setLayerKey(l.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${l.key === layerKey ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-ink/15 text-ink-soft hover:border-ink/30"}`}
            data-testid={`delivery-layer-${l.key}`}
          >
            {l.label}
          </button>
        ))}
      </div>
      {sections.length === 0 && <p className="text-ink-soft text-sm">Add sections and beats in Structure first.</p>}
      {sections.map((s) => (
        <SectionBeats key={s.section_id} workspaceId={workspaceId} presId={presId} sectionId={s.section_id} sectionTitle={s.title} layerKey={layerKey} annotationsByBeat={annotationsByBeat} />
      ))}
    </div>
  );
}
